'use client';

/**
 * CanadaOptOutBar — the top row on the Canadian surfaces:
 * "Already a StartEngine customer? Visit startengine.com →".
 *
 * Clicking writes `se_ca_optout=1` (30 days) so the visitor is not routed back
 * to the Canadian experience, then continues to startengine.com. The flag also
 * rides on the URL because a cookie cannot span .ca and .com — see
 * `data/canadaSite.ts`.
 */
import type { MouseEvent } from 'react';
import * as Sentry from 'utils/errorTracking';
import { styled } from '@mui/material/styles';
import { trackEvent } from 'utils/analytics';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import { buildOptOutUrl, setCanadaOptOutCookie } from '../data/canadaSite';

export interface CanadaOptOutBarProps {
  /** Analytics page key for the surface hosting the bar. */
  trackingPage: string;
  className?: string;
  'data-cy'?: string;
}

const Bar = styled('div')({
  background: COLORS.champagne,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  flexWrap: 'wrap',
  padding: '15px 20px',
  fontFamily: FONTS.sans,
  fontSize: '16px',
  fontWeight: 600,
  letterSpacing: '0.01em',
  lineHeight: 1.4,
  color: COLORS.night,
  textAlign: 'center',
  '@media (max-width: 640px)': {
    fontSize: '14px',
    padding: '13px 18px',
  },
});

const OptOutLink = styled('a')({
  fontFamily: FONTS.sans,
  fontSize: 'inherit',
  fontWeight: 800,
  letterSpacing: 'inherit',
  color: COLORS.night,
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  '&:hover': { color: COLORS.night, opacity: 0.72 },
  '&:focus-visible': {
    outline: `2px solid ${COLORS.night}`,
    outlineOffset: '3px',
  },
});

export function CanadaOptOutBar({
  trackingPage,
  className,
  'data-cy': dataCy = 'canada-opt-out-bar',
}: CanadaOptOutBarProps) {
  const handleClick = (_event: MouseEvent<HTMLAnchorElement>) => {
    try {
      setCanadaOptOutCookie();
      trackEvent('canada_opt_out_clicked', {
        page: trackingPage,
        surface: 'canada_opt_out_bar',
      }).catch(() => {});
    } catch (e) {
      // Never block the outbound navigation on a cookie or analytics failure.
      Sentry.captureException(e, { tags: { area: trackingPage } });
    }
  };

  return (
    <Bar className={className} data-cy={dataCy}>
      <span>Already a StartEngine customer?</span>
      <OptOutLink href={buildOptOutUrl()} onClick={handleClick} data-cy="canada-opt-out-link">
        Visit startengine.com →
      </OptOutLink>
    </Bar>
  );
}

export default CanadaOptOutBar;
